import {
  JSX,
  createSignal,
  createEffect,
  For,
  Show,
  createMemo,
} from 'solid-js';
import {
  clone,
  UnitSystemType,
  UnitSystems,
  IFormattingSettings,
  ILengthUnit,
  LengthUnit,
  IAngleUnit,
  AngleUnit,
  UnitRecordType,
  DECIMALS,
  FRACTIONS,
  Unit,
} from '@fulcrum/lib';
import {
  updateMeasurementsSetting,
  sameUnitForMeasurements as sameUnitForMeasurementsHelper,
} from './formatting-settings-helper';
import {
  FormattingSettings as ProjectFormattingSettings,
  SlopeMeasurementSettings,
  SlopeAngleFormat,
  FeetInchUnit,
  SlopeDisplayMode,
  DisplayMode,
  CoordinateSettings,
  CoordinateOrder,
} from '@trimble/types';
import { t } from '@trimble/i18n';
import { FormattingSettingsOptions, presetDisplayModes } from '@trimble/types';
import { UnitDisplaySettingsSelector } from './UnitDisplaySettingsSelector';

const slopeAngleFormats: Record<SlopeAngleFormat, string> = {
  ratio: 'shared.ui.unitSettings.slopeAngleFormatRatio',
  percentage: 'shared.ui.unitSettings.slopeAngleFormatPercentage',
  degrees: 'shared.ui.unitSettings.slopeAngleFormatDegrees',
};

const slopeDisplayModes: Record<SlopeDisplayMode, string> = {
  [DisplayMode.Slope]: 'shared.ui.unitSettings.slopeDisplayModeSlope',
  [DisplayMode.VerticalHorizontal]:
    'shared.ui.unitSettings.slopeDisplayModeHorizontalAndVertical',
};

export interface FormattingSettingsProps {
  settings: ProjectFormattingSettings;
  slopeMeasurementSettings?: SlopeMeasurementSettings;
  coordinateSettings?: CoordinateSettings;
  onSettingsChanged(settings: ProjectFormattingSettings): void;
  onSlopeMeasurementSettingsChanged?(settings: SlopeMeasurementSettings): void;
  onCoordinateSettingsChanged?(settings: CoordinateSettings): void;
  options?: FormattingSettingsOptions;
}

interface PrecisionSelectorProps {
  lengthFormattingSetting: keyof IFormattingSettings;
  lengthDecimalPrecisionSetting: keyof IFormattingSettings;
  lengthFractionPrecisionSetting: keyof IFormattingSettings;
  labelId: string;
}

interface UnitAndPrecisionSectionProps {
  label: string;
  lengthSelector: JSX.Element;
  precisionSelector: JSX.Element;
  id?: string;
}

interface SelectorProps {
  setting: keyof IFormattingSettings;
  labelId: string;
}

interface UnitSelectorProps extends SelectorProps {
  units: UnitRecordType;
  unitPrecisionSetting: keyof IFormattingSettings;
}

interface AngleSelectorProps extends SelectorProps {
  units: Partial<Record<AngleUnit, IAngleUnit>>;
  unitPrecisionSetting: keyof IFormattingSettings;
}

interface LengthSelectorProps {
  units: Partial<Record<LengthUnit, ILengthUnit>>;
  lengthSetting: keyof IFormattingSettings;
  lengthFormattingSetting: keyof IFormattingSettings;
  lengthPrecisionSetting: keyof IFormattingSettings;
  lengthSettingValue: LengthUnit;
  lengthFormattingSettingValue: 'decimal' | 'fractional';
}

interface LengthAndPrecisionSectionProps extends LengthSelectorProps {
  lengthFractionSetting: keyof IFormattingSettings;
  label: string;
}

export const FormattingSettings = (
  props: FormattingSettingsProps
): JSX.Element => {
  const lengthLabel = 'formatting-settings-length-label';
  const areaLabel = 'formatting-settings-area-label';
  const volumeLabel = 'formatting-settings-volume-label';
  const weightLabel = 'formatting-settings-weight-label';
  const angleLabel = 'formatting-settings-angle-label';
  const slopeLabel = 'formatting-settings-slope-label';
  const slopeDisplayModeLabel = 'formatting-settings-slope-display-mode-label';

  const [selectedCoordinateOrder, setSelectedCoordinateOrder] = createSignal<
    CoordinateOrder | undefined
  >(undefined);

  createEffect(() => {
    const order = props.coordinateSettings?.order;
    setSelectedCoordinateOrder(order);
  });

  const changeCoordinateSettings = (order: CoordinateOrder): void => {
    setSelectedCoordinateOrder(order);
    props.onCoordinateSettingsChanged?.({
      ...props.coordinateSettings,
      order: order,
    });
  };

  const [sameUnitForMeasurements, setSameUnitForMeasurements] = createSignal(
    // eslint-disable-next-line solid/reactivity
    sameUnitForMeasurementsHelper(props.settings)
  );

  const options = createMemo(() => props.options ?? presetDisplayModes['full']);

  // Update sameUnitForMeasurements when props change
  createEffect(() => {
    const settings = props.settings;
    setSameUnitForMeasurements(sameUnitForMeasurementsHelper(settings));
  });

  const changeSettings = (values: Partial<IFormattingSettings>): void => {
    let updatedValues = values as IFormattingSettings;
    if (
      sameUnitForMeasurements() &&
      Object.keys(values).some((key) => key.startsWith('length'))
    ) {
      updatedValues = clone(
        values,
        updateMeasurementsSetting(values as IFormattingSettings, props.settings)
      ) as IFormattingSettings;
    }
    props.onSettingsChanged(clone(props.settings, updatedValues));
  };

  const toggleMeasurementUnitSelection = (): void => {
    const newValue = !sameUnitForMeasurements();
    setSameUnitForMeasurements(newValue);

    if (newValue) {
      changeSettings({
        lengthMeasurementUnit: props.settings.lengthUnit,
        lengthMeasurementFormatting: props.settings.lengthFormatting,
        lengthMeasurementDecimals: props.settings.lengthDecimals,
        lengthMeasurementFractions: props.settings.lengthFractions,
      } as Partial<IFormattingSettings>);
    }
  };

  const unitSystemType = (): UnitSystemType => {
    return props.settings.unitSystem.toLowerCase() as UnitSystemType;
  };

  const unitSystem = (): (typeof UnitSystems)[UnitSystemType] => {
    return UnitSystems[unitSystemType()];
  };

  const LengthSelector = (selectorProps: LengthSelectorProps): JSX.Element => (
    <select
      id="formatting-settings-length-selector"
      aria-labelledby={lengthLabel}
      disabled={!options().enableUnitSelection}
      class="settings-select"
      onChange={(e) => {
        if (e.currentTarget.value in selectorProps.units) {
          changeSettings({
            [selectorProps.lengthFormattingSetting]: 'decimal',
            [selectorProps.lengthSetting]: e.currentTarget.value as LengthUnit,
            [selectorProps.lengthPrecisionSetting]:
              selectorProps.units[e.currentTarget.value as LengthUnit]
                ?.decimals ??
              props.settings[selectorProps.lengthPrecisionSetting],
          } as Partial<IFormattingSettings>);
        } else {
          changeSettings({
            [selectorProps.lengthSetting]: FeetInchUnit,
            [selectorProps.lengthFormattingSetting]: 'fractional',
          } as Partial<IFormattingSettings>);
        }
      }}
    >
      <For each={Object.entries(selectorProps.units)}>
        {([key, value]) => {
          if (
            key === 'in' &&
            ['imperial', 'custom'].includes(unitSystemType())
          ) {
            return (
              <>
                <option
                  class="select-option"
                  value={key}
                  selected={
                    selectorProps.lengthFormattingSettingValue === 'decimal' &&
                    selectorProps.lengthSettingValue === key
                  }
                >
                  {value.symbol}
                </option>
                <option
                  class="select-option"
                  value={FeetInchUnit}
                  selected={
                    selectorProps.lengthFormattingSettingValue === 'fractional'
                  }
                >
                  {FeetInchUnit}
                </option>
              </>
            );
          } else {
            return (
              <option
                class="select-option"
                value={key}
                selected={
                  selectorProps.lengthFormattingSettingValue === 'decimal' &&
                  selectorProps.lengthSettingValue === key
                }
              >
                {value.symbol}
              </option>
            );
          }
        }}
      </For>
    </select>
  );

  const UnitSelector = (selectorProps: UnitSelectorProps): JSX.Element => (
    <select
      class="settings-select"
      aria-labelledby={selectorProps.labelId}
      disabled={!options().enableUnitSelection}
      onChange={(e) => {
        const unit = e.currentTarget.value as Unit;
        const unitType = (
          selectorProps.units as Record<Unit, { decimals?: number }>
        )[unit];
        const precision =
          unitType.decimals ??
          props.settings[selectorProps.unitPrecisionSetting];
        changeSettings({
          [selectorProps.setting]: unit,
          [selectorProps.unitPrecisionSetting]: precision,
        } as Partial<IFormattingSettings>);
      }}
    >
      <For each={Object.entries(selectorProps.units)}>
        {([key, value]) => {
          return (
            <option
              class="select-option"
              value={key}
              selected={props.settings[selectorProps.setting] === key}
            >
              {value.symbol}
            </option>
          );
        }}
      </For>
    </select>
  );

  const AngleSelector = (selectorProps: AngleSelectorProps): JSX.Element => (
    <select
      disabled={!options().enableUnitSelection}
      class="settings-select"
      aria-labelledby={selectorProps.labelId}
      onChange={(e) => {
        changeSettings({
          [selectorProps.setting]: e.currentTarget.value,
          [selectorProps.unitPrecisionSetting]:
            selectorProps.units[e.currentTarget.value as AngleUnit]?.decimals ??
            props.settings[selectorProps.unitPrecisionSetting],
        } as Partial<IFormattingSettings>);
      }}
    >
      <For each={Object.entries(selectorProps.units)}>
        {([key]) => {
          return (
            <option
              class="select-option"
              value={key}
              selected={props.settings[selectorProps.setting] === key}
            >
              {key}
            </option>
          );
        }}
      </For>
    </select>
  );

  /**
   * FRACTIONS items are strings like "1/2", "1/4", etc. The first two characters ("1/") are a prefix,
   * so we slice from index 2 to extract the denominator value (e.g., "16" from "1/16").
   */
  const FRACTION_SLICE_START = 2;

  const FractionsSelector = (selectorProps: SelectorProps): JSX.Element => (
    <select
      disabled={!options().enablePrecisionSelection}
      class="settings-select"
      aria-labelledby={selectorProps.labelId}
      onChange={(e) => {
        changeSettings({
          [selectorProps.setting]: Number.parseInt(
            e.currentTarget.value.slice(FRACTION_SLICE_START),
            10
          ),
        } as Partial<IFormattingSettings>);
      }}
    >
      <For each={FRACTIONS}>
        {(item) => {
          return (
            <option
              class="select-option"
              value={String(item)}
              selected={
                props.settings[selectorProps.setting] ===
                Number.parseInt(item.slice(FRACTION_SLICE_START), 10)
              }
            >
              {item}
            </option>
          );
        }}
      </For>
    </select>
  );

  const DecimalsSelector = (selectorProps: SelectorProps): JSX.Element => (
    <select
      disabled={!options().enablePrecisionSelection}
      class="settings-select"
      aria-labelledby={selectorProps.labelId}
      onChange={(e) => {
        changeSettings({
          [selectorProps.setting]: DECIMALS.indexOf(
            Number.parseFloat(e.currentTarget.value)
          ),
        } as Partial<IFormattingSettings>);
      }}
    >
      <For each={DECIMALS}>
        {(item) => (
          <option
            class="select-option"
            value={String(item)}
            selected={
              props.settings[selectorProps.setting] === DECIMALS.indexOf(item)
            }
          >
            {item}
          </option>
        )}
      </For>
    </select>
  );

  const PrecisionSelector = (
    selectorProps: PrecisionSelectorProps
  ): JSX.Element => (
    <>
      <Show
        when={
          props.settings[selectorProps.lengthFormattingSetting] === 'decimal'
        }
      >
        <DecimalsSelector
          setting={selectorProps.lengthDecimalPrecisionSetting}
          labelId={selectorProps.labelId}
        />
      </Show>
      <Show
        when={
          props.settings[selectorProps.lengthFormattingSetting] === 'fractional'
        }
      >
        <FractionsSelector
          setting={selectorProps.lengthFractionPrecisionSetting}
          labelId={selectorProps.labelId}
        />
      </Show>
    </>
  );

  const SlopeAngleFormatSelector = (): JSX.Element =>
    props.slopeMeasurementSettings && (
      <div class="input-group">
        <label id={slopeLabel} class={'modus-wc-text-base'}>
          {t('shared.ui.unitSettings.slopeAngleFormat')}
        </label>
        <div class="mt-[0.2rem]">
          <select
            disabled={!options().enableUnitSelection}
            aria-labelledby={slopeLabel}
            onChange={(e) => {
              props.onSlopeMeasurementSettingsChanged?.({
                format: e.currentTarget.value as SlopeAngleFormat,
                displayMode: props.slopeMeasurementSettings!.displayMode,
              });
            }}
          >
            <For each={Object.entries(slopeAngleFormats)}>
              {([key, value]) => (
                <option
                  class="select-option"
                  value={key}
                  selected={props.slopeMeasurementSettings!.format === key}
                >
                  {t(value)}
                </option>
              )}
            </For>
          </select>
        </div>
      </div>
    );

  const SlopeDisplayModeSelector = (): JSX.Element =>
    props.slopeMeasurementSettings && (
      <div class="input-group">
        <label id={slopeDisplayModeLabel} class={'modus-wc-text-base'}>
          {t('shared.ui.unitSettings.slopeDisplayMode')}
        </label>
        <div class="mt-[0.2rem]">
          <select
            disabled={!options().enableUnitSelection}
            aria-labelledby={slopeDisplayModeLabel}
            onChange={(e) => {
              props.onSlopeMeasurementSettingsChanged?.({
                format: props.slopeMeasurementSettings!.format,
                displayMode: Number.parseInt(
                  e.currentTarget.value,
                  10
                ) as SlopeDisplayMode,
              });
            }}
          >
            <For each={Object.entries(slopeDisplayModes)}>
              {([key, value]) => (
                <option
                  class="select-option"
                  value={key}
                  selected={
                    props.slopeMeasurementSettings!.displayMode ===
                    Number.parseInt(key, 10)
                  }
                >
                  {t(value)}
                </option>
              )}
            </For>
          </select>
        </div>
      </div>
    );

  const LengthSection = (): JSX.Element => {
    return (
      <LengthAndPrecisionSection
        units={unitSystem().Length}
        lengthSetting="lengthUnit"
        lengthFormattingSetting="lengthFormatting"
        lengthPrecisionSetting="lengthDecimals"
        lengthFractionSetting="lengthFractions"
        lengthSettingValue={props.settings.lengthUnit}
        lengthFormattingSettingValue={props.settings.lengthFormatting}
        label={t('shared.ui.unitSettings.lengthUnit')}
      />
    );
  };

  const MeasurementLengthSection = (): JSX.Element => {
    return (
      <LengthAndPrecisionSection
        units={unitSystem().Length}
        lengthSetting="lengthMeasurementUnit"
        lengthFormattingSetting="lengthMeasurementFormatting"
        lengthPrecisionSetting="lengthMeasurementDecimals"
        lengthFractionSetting="lengthMeasurementFractions"
        lengthSettingValue={props.settings.lengthMeasurementUnit}
        lengthFormattingSettingValue={
          props.settings.lengthMeasurementFormatting
        }
        label={t('shared.ui.unitSettings.measurementLengthUnit')}
      />
    );
  };

  const LengthAndPrecisionSection = (
    props: LengthAndPrecisionSectionProps
  ): JSX.Element => (
    <div class="input-group">
      <label id={lengthLabel} class={'modus-wc-text-base'}>
        {props.label}
      </label>
      <div class="flex gap-4 mt-[0.2rem]">
        <div class="w-1/2">
          <LengthSelector
            units={props.units}
            lengthSetting={props.lengthSetting}
            lengthFormattingSetting={props.lengthFormattingSetting}
            lengthPrecisionSetting={props.lengthPrecisionSetting}
            lengthSettingValue={props.lengthSettingValue}
            lengthFormattingSettingValue={props.lengthFormattingSettingValue}
          />
        </div>
        <div class="w-1/2">
          <PrecisionSelector
            lengthFormattingSetting={props.lengthFormattingSetting}
            lengthDecimalPrecisionSetting={props.lengthPrecisionSetting}
            lengthFractionPrecisionSetting={props.lengthFractionSetting}
            labelId={lengthLabel}
          />
        </div>
      </div>
    </div>
  );

  const UnitAndPrecisionSection = (
    props: UnitAndPrecisionSectionProps
  ): JSX.Element => (
    <div class="input-group">
      <label id={props.id} class={'modus-wc-text-base'}>
        {props.label}
      </label>
      <div class="flex gap-4 mt-[0.2rem]">
        <div class="w-1/2">{props.lengthSelector}</div>
        <div class="w-1/2">{props.precisionSelector}</div>
      </div>
    </div>
  );

  return (
    <div data-testid="formatting-settings" class="flex w-full flex-col gap-y-2">
      <UnitDisplaySettingsSelector
        settings={props.settings}
        coordinateSettings={props.coordinateSettings}
        onCoordinateSettingsChanged={changeCoordinateSettings}
        onSettingsChanged={changeSettings}
        selectedCoordinateOrder={selectedCoordinateOrder()}
        options={options()}
      />
      {/* Length Unit and Precision Header */}
      <div class="flex w-full pt-2 gap-4 modus-wc-text-lg modus-wc-font-semibold">
        <span class="w-1/2">{t('shared.ui.unitSettings.unit')}</span>
        <span class="w-1/2">
          {t('shared.ui.unitSettings.displayPrecision')}
        </span>
      </div>

      {/* Length Unit and Precision Selector */}
      <LengthSection />

      {/* Same Measurement Unit Toggle - disable in Civil3DViewer for now, we might want to use this in the future */}
      <Show when={options().enableUnitSelection}>
        <div class="flex gap-2">
          <modus-wc-switch
            value={sameUnitForMeasurements()}
            on:inputChange={toggleMeasurementUnitSelection}
          />
          <label>{t('shared.ui.unitSettings.sameMeasurementUnit')}</label>
        </div>
      </Show>

      {/* Measurement Length Unit and Precision Selector */}
      <Show when={!sameUnitForMeasurements()}>
        <MeasurementLengthSection />
      </Show>

      {/* Area Section */}
      <UnitAndPrecisionSection
        label={t('shared.ui.unitSettings.area')}
        lengthSelector={
          <UnitSelector
            units={unitSystem().Area}
            setting="areaUnit"
            unitPrecisionSetting="areaDecimals"
            labelId={areaLabel}
          />
        }
        precisionSelector={
          <DecimalsSelector setting="areaDecimals" labelId={areaLabel} />
        }
        id={areaLabel}
      />

      {/* Volume Section */}
      <UnitAndPrecisionSection
        label={t('shared.ui.unitSettings.volume')}
        lengthSelector={
          <UnitSelector
            units={unitSystem().Volume}
            setting="volumeUnit"
            unitPrecisionSetting="volumeDecimals"
            labelId={volumeLabel}
          />
        }
        precisionSelector={
          <DecimalsSelector setting="volumeDecimals" labelId={volumeLabel} />
        }
        id={volumeLabel}
      />

      {/* Weight Section */}
      <UnitAndPrecisionSection
        label={t('shared.ui.unitSettings.weight')}
        lengthSelector={
          <UnitSelector
            units={unitSystem().Mass}
            setting="massUnit"
            unitPrecisionSetting="massDecimals"
            labelId={weightLabel}
          />
        }
        precisionSelector={
          <DecimalsSelector setting="massDecimals" labelId={weightLabel} />
        }
        id={weightLabel}
      />

      {/* Angle Section */}
      <UnitAndPrecisionSection
        label={t('shared.ui.unitSettings.angle')}
        lengthSelector={
          <AngleSelector
            units={unitSystem().Angle}
            setting="angleUnit"
            unitPrecisionSetting="angleDecimals"
            labelId={angleLabel}
          />
        }
        precisionSelector={
          <DecimalsSelector setting="angleDecimals" labelId={angleLabel} />
        }
      />
      <Show when={props.slopeMeasurementSettings}>
        <SlopeAngleFormatSelector />
        <SlopeDisplayModeSelector />
      </Show>
    </div>
  );
};
